import type { ColDef } from "ag-grid-enterprise";
import { ForecastMetaDataType } from "../../../hooks/useFetchForecastMetaData";
import type { ForecastTableControlValues } from "../../tableControls/forecastTableControlValues";
import type { CubeWindow } from "../utils/columnUtils";
import { createColumnsPinnedLeft } from "./shared/columnsPinnedLeft";
import { createColumnsPinnedRight } from "./shared/columnsPinnedRight";
import { createHalfYearlyColumns } from "./variants/columnsHalfYearly";
import { createMonthlyColumns } from "./variants/columnsMonthly";
import { createPTGColumns } from "./variants/columnsPTG";
import { createQuarterlyColumns } from "./variants/columnsQuarterly";
import { createWeeklyColumns } from "./variants/columnsWeekly";

export type ColumnType =
  | "weekly"
  | "monthly"
  | "quarterly"
  | "half-yearly"
  | "PTG";
export type CalendarType = "inventory_454" | "fiscal_gregorian" | "gregorian";

/** Shared options for time-period column creation. */
export interface TimePeriodColumnOptions {
  planBy: ForecastTableControlValues["planBy"];
  planningYear: string;
  metaData: ForecastMetaDataType;
  /**
   * Which label weekly headers display: the calendar label resolved via
   * ``gregMapping``, or the fiscal column id itself (``W01`` = first
   * fiscal week — the MandM default, DAS-6481). Display-only: column
   * ids, tooltips, and the current-date line are unaffected.
   */
  weekLabels: ForecastTableControlValues["weekLabels"];
  intake: ForecastTableControlValues["intake"] | undefined;
  /**
   * Producer type whose rows are editable. ``undefined`` disables edits
   * entirely (used for frozen budget cubes). Callers must pass this
   * explicitly — omitting it would silently edit-enable the wrong
   * producer on a producer-flipped page.
   *
   * Decision N widens the literal type to ``string`` so the range-plan
   * modal can use its own producer label (``"user"`` for the planner's
   * edits, ``"reference"`` for the un-edited baseline). Forecast / budget
   * keep passing ``"user"`` / ``"budget"``.
   */
  editableProducer: string | undefined;
  /**
   * Decision N: per-row editability narrowing. When set, a cell is
   * editable only if the existing metric/producer/window checks pass AND
   * this predicate returns true for the row. Forecast's existing caller
   * leaves this undefined → behaviour unchanged.
   */
  editableRowPredicate?: (row: { metric_name: string }) => boolean;
  /**
   * Decision N: optional whitelist that replaces the cube's
   * ``metricEditableMapping`` for the per-metric edit gate. Range plan
   * plans by NIS3 (decision G); the cube marks NIS3 as read-only
   * because the forecast page plans by GS1 / intake. Passing
   * ``new Set(["NIS3"])`` here unlocks NIS3 without affecting forecast
   * callers (who leave this undefined). All other gates still apply.
   */
  editableMetricsOverride?: ReadonlySet<string>;
  /**
   * Decision N: per-cell time-window override. When set, this predicate
   * replaces the default forecast-page "current week only" gate
   * (``isEditableWithinTimeWindow``) — the metric / producer / row
   * gates from {@link isCellEditAllowed} still apply, and the cell is
   * editable iff this predicate also returns ``true``. Lets the range-
   * plan modal allow per-week edits for the entire forward horizon
   * (``year_week >= week_from``) without affecting forecast pages,
   * which leave this undefined.
   */
  editableTimeWindowPredicate?: (cell: {
    metric_name: string;
    producer_type: string;
    fiscal_year_name: string;
    colId: string;
  }) => boolean;
  /**
   * Decision N: optional per-column hint to make the weekly column-header
   * tooltips identical across the modal grid and the main range-plan
   * grid. The main grid passes the YYYYWW it enumerates directly; the
   * modal grid passes a resolver that picks the calendar year for the
   * planner's *current* fiscal-year pair (since the modal's column
   * headers are shared across multiple FY rows). Forecast / budget
   * callers leave this undefined — their column headers span a single
   * fiscal year so the date is already implied.
   */
  headerTooltipYearWeekResolver?: (
    calendarWeekKey: string
  ) => number | undefined;
  /**
   * The save cube's week window (from its ``CubeVersion``). Cells whose
   * backing weeks fall outside it are read-only and dimmed — the save
   * rejects them. Undefined leaves the gate off (read-only grids, callers
   * without a save cube).
   */
  cubeWindow?: CubeWindow;
}

export function createColumnDefinitions(
  type: ColumnType,
  calendarType: CalendarType,
  options: TimePeriodColumnOptions
): ColDef[] {
  return [
    ...createColumnsPinnedLeft(calendarType, options.metaData),
    ...createTimePeriodColumnsByType(type, calendarType, options),
    ...createColumnsPinnedRight(options.metaData)
  ];
}

function createTimePeriodColumnsByType(
  type: ColumnType,
  calendarType: CalendarType,
  options: TimePeriodColumnOptions
): ColDef[] {
  switch (type) {
    case "weekly":
      return createWeeklyColumns(options);
    case "monthly":
      return createMonthlyColumns(calendarType, options);
    case "quarterly":
      return createQuarterlyColumns(calendarType, options);
    case "half-yearly":
      return createHalfYearlyColumns(calendarType, options);
    case "PTG":
      return createPTGColumns(options);
  }
}
